import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TimePicker } from '@/components/ui/time-picker';
import type { IssuanceType } from '@/types/certificate.types';
import SearchUser from '@/components/settings/search-user';
import { RoleSelector } from '@/components/ui/role-selector';

type ManualReleaseType = 'STATIC' | 'DYNAMIC' | 'NOT_APPLICABLE';

interface IssuanceConfigurationProps {
  issuanceType: IssuanceType;
  releaseDate?: string | null;
  onIssuanceTypeChange: (type: IssuanceType) => void;
  onReleaseDateChange: (date: string | null) => void;
  applicantCanAccess: boolean;
  onApplicantCanAccessChange: (value: boolean) => void;
  manualReleaseType: ManualReleaseType;
  onManualReleaseTypeChange: (value: ManualReleaseType) => void;
  manualReleaseUserId?: string | null;
  onManualReleaseUserChange: (userId: string | null) => void;
  manualReleaseRoles: string[];
  onManualReleaseRolesChange: (roles: string[]) => void;
}

const parseTimeFromDate = (date: Date): string => {
  const hours24 = date.getHours();
  const minutes = date.getMinutes();
  const period: 'AM' | 'PM' = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
  return `${String(hours12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

const combineDateTime = (date: Date, timeValue: string): string => {
  const parts = timeValue.split(' ');
  if (parts.length !== 2) return date.toISOString();
  const [timePart, period] = parts;
  const [h, m] = timePart.split(':').map(Number);
  let hour24 = h;
  if (period === 'PM' && h !== 12) hour24 = h + 12;
  if (period === 'AM' && h === 12) hour24 = 0;
  const combined = new Date(date);
  combined.setHours(hour24, m, 0, 0);
  return combined.toISOString();
};

const IssuanceConfiguration: React.FC<IssuanceConfigurationProps> = ({
  issuanceType,
  releaseDate,
  onIssuanceTypeChange,
  onReleaseDateChange,
  applicantCanAccess,
  onApplicantCanAccessChange,
  manualReleaseType,
  onManualReleaseTypeChange,
  manualReleaseUserId,
  onManualReleaseUserChange,
  manualReleaseRoles,
  onManualReleaseRolesChange,
}) => {
  const { t } = useTranslation();

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    if (releaseDate) {
      const d = new Date(releaseDate);
      return isNaN(d.getTime()) ? undefined : d;
    }
    return undefined;
  });

  const [timeValue, setTimeValue] = useState<string>(() => {
    if (releaseDate) {
      const d = new Date(releaseDate);
      return isNaN(d.getTime()) ? '' : parseTimeFromDate(d);
    }
    return '';
  });

  useEffect(() => {
    if (!releaseDate) {
      setSelectedDate(undefined);
      setTimeValue('');
      return;
    }
    const d = new Date(releaseDate);
    if (!isNaN(d.getTime())) {
      setSelectedDate(d);
      setTimeValue(parseTimeFromDate(d));
    }
  }, [releaseDate]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date && timeValue) {
      onReleaseDateChange(combineDateTime(date, timeValue));
    } else if (date) {
      onReleaseDateChange(date.toISOString());
    } else {
      onReleaseDateChange(null);
    }
  };

  const handleTimeChange = (value: string) => {
    setTimeValue(value);
    if (selectedDate && value) {
      onReleaseDateChange(combineDateTime(selectedDate, value));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('certificate.issuanceConfiguration.title')}</CardTitle>
        <CardDescription>{t('certificate.issuanceConfiguration.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <RadioGroup
          value={issuanceType}
          onValueChange={(v) => {
            onIssuanceTypeChange(v as IssuanceType);
            if (v !== 'RELEASE_DATE') onReleaseDateChange(null);
          }}
          className="space-y-4"
        >
          <div className="flex items-start space-x-3 p-4 border rounded-lg">
            <RadioGroupItem value="IMMEDIATE" id="issuance-immediate" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="issuance-immediate" className="font-medium cursor-pointer">
                {t('certificate.issuanceConfiguration.immediate')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                {t('certificate.issuanceConfiguration.immediateDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 border rounded-lg">
            <RadioGroupItem value="RELEASE_DATE" id="issuance-release-date" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="issuance-release-date" className="font-medium cursor-pointer">
                {t('certificate.issuanceConfiguration.releaseDate')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                {t('certificate.issuanceConfiguration.releaseDateDescription')}
              </p>
              {issuanceType === 'RELEASE_DATE' && (
                <div className="mt-3 space-y-2">
                  <Label className="text-sm font-medium">
                    {t('certificate.issuanceConfiguration.selectReleaseDate')}
                  </Label>
                  <div className="flex gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex-1 rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between items-center text-[#5A5A5A] bg-[#F6F9FF] text-sm outline-none hover:bg-[#E8F2FF] transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            {selectedDate ? (
                              <span className="text-[#1A1A1A]">{format(selectedDate, 'LLL dd, y')}</span>
                            ) : (
                              <span className="text-[#9CA3AF]">{t('forms.pickADate')}</span>
                            )}
                          </div>
                          <CalendarIcon className="h-4 w-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          captionLayout="dropdown-buttons"
                          fromDate={new Date()}
                          fromYear={new Date().getFullYear()}
                          toYear={2100}
                        />
                      </PopoverContent>
                    </Popover>
                    <TimePicker
                      value={timeValue}
                      onChange={handleTimeChange}
                      placeholder={t('forms.pickATime')}
                      format="12h"
                      className="flex-1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 border rounded-lg">
            <RadioGroupItem value="MANUAL" id="issuance-manual" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="issuance-manual" className="font-medium cursor-pointer">
                {t('certificate.issuanceConfiguration.manual')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                {t('certificate.issuanceConfiguration.manualDescription')}
              </p>

              {issuanceType === 'MANUAL' && (
                <div className="mt-4 space-y-3 border-t pt-3">
                  <Label className="text-sm font-medium">
                    {t('certificate.issuanceConfiguration.whoCanRelease')}
                  </Label>
                  <RadioGroup
                    value={manualReleaseType}
                    onValueChange={(v) => {
                      const next = v as ManualReleaseType;
                      onManualReleaseTypeChange(next);
                      // Drop the setting that no longer applies
                      if (next !== 'STATIC') onManualReleaseUserChange(null);
                      if (next !== 'DYNAMIC') onManualReleaseRolesChange([]);
                    }}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="NOT_APPLICABLE" id="release-anyone" />
                      <Label htmlFor="release-anyone" className="cursor-pointer text-sm">
                        {t('certificate.issuanceConfiguration.anyAuthorisedStaff')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="STATIC" id="release-static" />
                      <Label htmlFor="release-static" className="cursor-pointer text-sm">
                        {t('certificate.issuanceConfiguration.oneChosenPerson')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="DYNAMIC" id="release-dynamic" />
                      <Label htmlFor="release-dynamic" className="cursor-pointer text-sm">
                        {t('certificate.issuanceConfiguration.anyoneWithRole')}
                      </Label>
                    </div>
                  </RadioGroup>

                  {manualReleaseType === 'STATIC' && (
                    <div className="max-w-md">
                      <SearchUser
                        setUserId={(id: any) => onManualReleaseUserChange(id ?? null)}
                        selectedUserId={manualReleaseUserId ?? undefined}
                      />
                    </div>
                  )}

                  {manualReleaseType === 'DYNAMIC' && (
                    <RoleSelector
                      selectedRoles={manualReleaseRoles}
                      onRolesChange={onManualReleaseRolesChange}
                      placeholder={t('certificate.issuanceConfiguration.selectRoles')}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </RadioGroup>

        <div className="flex items-start space-x-3 p-4 border rounded-lg">
          <input
            type="checkbox"
            id="applicant-can-access"
            className="mt-1 h-4 w-4"
            checked={applicantCanAccess}
            onChange={(e) => onApplicantCanAccessChange(e.target.checked)}
          />
          <div className="flex-1">
            <Label htmlFor="applicant-can-access" className="font-medium cursor-pointer">
              {t('certificate.issuanceConfiguration.applicantCanAccess')}
            </Label>
            <p className="text-sm text-gray-500 mt-1">
              {t('certificate.issuanceConfiguration.applicantCanAccessDescription')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default IssuanceConfiguration;
